import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartItem, CartState } from '../../types/cart.types';

const loadCartItems = (): CartItem[] => {
  try {
    const saved = localStorage.getItem('cart_items');
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
};

const saveCartItems = (items: CartItem[]) => {
  try {
    localStorage.setItem('cart_items', JSON.stringify(items));
  } catch (e) {
    console.error('Failed to save cart items', e);
  }
};

// Initial state loaded from localStorage
const initialItems = loadCartItems();
const initialSubtotal = initialItems.reduce((acc, item) => {
  const price = item.product.discountedPrice || item.product.price;
  return acc + (price * item.quantity);
}, 0);
const initialTax = initialSubtotal * 0.18;
const initialShipping = initialSubtotal > 1000 || initialSubtotal === 0 ? 0 : 50;

const initialState: CartState = {
  items: initialItems,
  isLoading: false,
  error: null,
  subtotal: initialSubtotal,
  total: initialSubtotal + initialTax + initialShipping,
  itemCount: initialItems.reduce((acc, item) => acc + item.quantity, 0),
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCartStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    addToCartSuccess: (state, action: PayloadAction<CartItem>) => {
      state.isLoading = false;
      const existingItem = state.items.find(item => item.productId === action.payload.productId);
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
      } else {
        state.items.push(action.payload);
      }
      
      state.error = null;
      cartSlice.caseReducers.calculateTotals(state);
      saveCartItems(state.items);
    },
    addToCartFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      cartSlice.caseReducers.calculateTotals(state);
      saveCartItems(state.items);
    },
    updateQuantity: (state, action: PayloadAction<{ itemId: string; quantity: number }>) => {
      const item = state.items.find(item => item.id === action.payload.itemId);
      if (item) {
        item.quantity = action.payload.quantity;
        cartSlice.caseReducers.calculateTotals(state);
        saveCartItems(state.items);
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.subtotal = 0;
      state.total = 0;
      state.itemCount = 0;
      saveCartItems([]);
    },
    calculateTotals: (state) => {
      state.subtotal = state.items.reduce((total, item) => {
        const price = item.product.discountedPrice || item.product.price;
        return total + (price * item.quantity);
      }, 0);
      
      state.itemCount = state.items.reduce((total, item) => total + item.quantity, 0);
      
      const tax = state.subtotal * 0.18; // 18% GST
      const shipping = state.subtotal > 1000 || state.subtotal === 0 ? 0 : 50;
      state.total = state.subtotal + tax + shipping;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  addToCartStart,
  addToCartSuccess,
  addToCartFailure,
  removeFromCart,
  updateQuantity,
  clearCart,
  clearError,
} = cartSlice.actions;

export default cartSlice.reducer;
